import { IMAGES } from "@/site";
import { PageHero, Section, CTABand } from "@/components/ui-blocks/Blocks";
import { Eye, AlertOctagon, Users, TrainFront, CornerUpRight, ChevronsLeft, Siren, Gauge } from "lucide-react";

const TOPICS = [
  {
    icon: Eye,
    title: "Mirror Use",
    intro: "One of the most important safety habits for a school bus driver.",
    items: ["Flat mirrors","Convex mirrors","Crossover mirrors","Student mirrors","Blind spots","Mirror adjustment","Mirror scanning patterns","Before moving","Before turns","Before lane changes","Before backing","During loading/unloading"],
  },
  {
    icon: AlertOctagon,
    title: "Danger Zones",
    intro: "Areas around the bus where students are most difficult to see.",
    items: ["Front danger zone","Rear danger zone","Left side danger zone","Right side danger zone","Student visibility","Safe crossing procedures","Counting students","Mirror checks before moving","Never moving until all students are accounted for"],
  },
  {
    icon: Users,
    title: "Loading and Unloading",
    intro: "One of the highest-risk parts of school bus operation.",
    items: ["Approaching the stop","Activating warning lights","Checking traffic","Checking mirrors","Securing the bus","Opening the door","Counting students","Watching students cross","Confirming danger zones are clear","Watching for late students","Authorized receiver procedures","Never rushing the stop procedure"],
  },
  {
    icon: TrainFront,
    title: "Railroad Crossings",
    intro: "Procedures require full attention and discipline.",
    items: ["Approaching the crossing","Stopping at proper distance","Securing the bus","Opening door/window as required","Looking and listening","Checking tracks","Crossing safely","Never shifting on tracks","Handling malfunctioning gates","Emergency procedures if the bus stalls"],
  },
  {
    icon: CornerUpRight,
    title: "Turns & Tail Swing",
    intro: "School buses require careful space management during turns.",
    items: ["Right turns","Left turns","Lane positioning","Off-tracking","Tail swing","Curb clearance","Pedestrian awareness","Parked vehicle awareness","Mirror checks throughout the turn","Avoiding fixed objects"],
  },
  {
    icon: ChevronsLeft,
    title: "Backing Safety",
    intro: "Avoid backing whenever possible, but know how to back safely.",
    items: ["Avoiding unnecessary backing","Using a spotter when available","Checking mirrors","Sounding horn if required","Backing slowly","Stopping when unsure","Watching blind spots","Never backing into traffic without proper control"],
  },
  {
    icon: Siren,
    title: "Emergency Procedures",
    intro: "Drivers must be prepared for emergencies.",
    items: ["Emergency evacuation","Fire response","Crash response","Breakdown procedures","Student accountability","Securing the scene","Communication with dispatch","Post-incident reporting","Emergency equipment use"],
  },
  {
    icon: Gauge,
    title: "Defensive Driving",
    intro: "Helps prevent incidents before they happen.",
    items: ["Looking ahead","Scanning intersections","Managing speed","Managing space","Anticipating hazards","Avoiding distractions","Staying calm under pressure","Making safe decisions early","Adjusting for weather","Adjusting for traffic"],
  },
];

export default function SafetyTopics() {
  return (
    <>
      <PageHero
        testid="safety-hero"
        eyebrow="School Bus Safety Topics"
        title="Eight pillars of safe school transportation."
        description="The key safety topics school bus drivers need to understand before transporting students."
        image={IMAGES.safetyTopics}
      />

      <Section testid="safety-grid">
        <div className="grid md:grid-cols-2 gap-6">
          {TOPICS.map((t, i) => (
            <div key={t.title} data-testid={`safety-topic-${i}`} className="border border-white/10 bg-[#0c0c0e] p-7 md:p-8 hover:border-[#FFCC00]/50 transition-colors">
              <div className="flex items-start justify-between mb-5">
                <t.icon size={32} className="text-[#FFCC00]" strokeWidth={1.4} />
                <span className="font-mono text-[10px] text-white/40">{String(i + 1).padStart(2, "0")} / 08</span>
              </div>
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight">{t.title}</h3>
              <p className="mt-2 text-sm text-white/60">{t.intro}</p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {t.items.map((it) => (
                  <li key={it} className="text-sm text-white/80 flex items-start gap-2">
                    <span className="text-[#FFCC00] mt-1">▸</span> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <CTABand />
    </>
  );
}
